import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Event } from '@nostr/tools/wasm'
import { HugeiconsIcon } from '@hugeicons/react'
import { Router } from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'
import { useRef, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import client from '@/services/client.service'
import Note from './Note'
import NoteStats from './NoteStats'
import ReactionList from './ReactionList'
import ReplyNoteList, { findAuthorChain } from './ReplyNoteList'
import RelaySelector, { Item, SelectableItem } from './RelaySelector'
import { loadRelayList } from '@nostr/gadgets/lists'
import { getRoot, isProtectedEvent } from '@/lib/event'
import { repliesMapAtom } from '@/atoms/replies'
import { unique } from '@nostr/gadgets/utils'
import { useAsync } from 'react-use'
import { useAtomValue } from 'jotai'
import { unwrappedFavoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { useCurrentPubkey } from '@/atoms/session'

export default function NoteInteractions({
  pageIndex,
  event
}: {
  pageIndex?: number
  event: Event
}) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const repliesMap = useAtomValue(repliesMapAtom)
  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)
  const [type, setType] = useState<'replies' | 'reactions'>('replies')
  const relaySelection = useRef<SelectableItem[]>()
  const seenOn = isProtectedEvent(event) ? client.getSeenEventRelayUrls(event.id, event) : []

  const { value, loading } = useAsync(async () => {
    const relays: string[] = []

    const baseRelaysP = loadRelayList(event.pubkey)
    const pubkeyRelaysP = pubkey ? loadRelayList(pubkey) : Promise.resolve(undefined)
    const rootRelaysP = getRoot(event).then((rootPointer) =>
      rootPointer
        ? 'pubkey' in rootPointer
          ? loadRelayList(rootPointer.pubkey)
          : 'author' in rootPointer && rootPointer.author
            ? loadRelayList(rootPointer.author)
            : 'value' in rootPointer
              ? undefined
              : client
                  .fetchEvent(rootPointer)
                  .then((root) => (root ? loadRelayList(root.pubkey) : undefined))
        : undefined
    )

    for (const url of window.hallway.universe.readRepliesFrom ?? []) {
      relays.push(url)
    }
    for (const { url } of (await baseRelaysP).items.filter((url) => url.read)) {
      relays.push(url)
    }
    for (const { url } of (await rootRelaysP)?.items.filter((url) => url.read) ?? []) {
      relays.push(url)
    }

    const defaultRelays = new Set(relays)

    for (const { url } of (await pubkeyRelaysP)?.items.filter((url) => url.read) ?? []) {
      relays.push(url)
    }

    const items: Item[] = unique(relays.concat(seenOn)).map((url) => ({ kind: 'relay', url }))
    items.push({ kind: 'separator' })

    const relaySetItems: Extract<Item, { kind: 'relay-set' }>[] = []
    for (const fav of favorites ?? []) {
      if (typeof fav === 'string') {
        items.push({ kind: 'relay', url: fav })
      } else {
        relaySetItems.push({
          kind: 'relay-set',
          identifier: fav.pointer.identifier,
          relays: fav.items,
          name: fav.title
        })
      }
    }

    if (relaySetItems.length) {
      items.push({ kind: 'separator' })
      items.push(...relaySetItems)
    }

    return {
      relayOptions: items,
      initialSelection: window.hallway.universe.readRepliesFromExclusive
        ? new Set(window.hallway.universe.readRepliesFrom || [])
        : defaultRelays
    }
  }, [event, favorites])

  const [selectedRelays, setSelectedRelays] = useState<string[] | undefined>()

  const authorChain = useMemo(() => findAuthorChain(repliesMap, event), [repliesMap, event])

  return (
    <>
      {authorChain.length > 0 && (
        <div className="divide-y">
          {authorChain.map((evt) => (
            <div key={evt.id} className="px-4 pt-3 pb-3">
              <Note event={evt} showFull hideParentNotePreview />
              <NoteStats className="mt-2" event={evt} />
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <ScrollArea className="flex-1 w-0">
          <Tabs selectedTab={type} onTabChange={setType} />
          <ScrollBar orientation="horizontal" className="opacity-0 pointer-events-none" />
        </ScrollArea>
        <Separator orientation="vertical" className="h-6" />
        <div className="flex items-center">
          {!loading && (
            <RelaySelector
              selected={value?.initialSelection || new Set()}
              onChange={(sel) => {
                relaySelection.current = sel
              }}
              onClose={() => {
                if (relaySelection.current)
                  setSelectedRelays(
                    relaySelection.current?.flatMap?.((item) =>
                      item.kind === 'relay'
                        ? item.url
                        : item.kind === 'relay-set'
                          ? item.relays
                          : []
                    ) || []
                  )
              }}
              items={value?.relayOptions || []}
            >
              <div className="size-10 flex items-center justify-center">
                <Button variant="ghost" size="icon" title={t('Relays')}>
                  <HugeiconsIcon icon={Router} className="text-muted-foreground" />
                </Button>
              </div>
            </RelaySelector>
          )}
        </div>
      </div>
      <Separator />
      <div className={type === 'replies' ? '' : 'hidden'}>
        <ReplyNoteList index={pageIndex} event={event} selectedRelays={selectedRelays} />
      </div>
      <div className={type === 'reactions' ? '' : 'hidden'}>
        <ReactionList event={event} selectedRelays={selectedRelays} />
      </div>
    </>
  )
}

export function Tabs({
  selectedTab,
  onTabChange
}: {
  selectedTab: 'replies' | 'reactions'
  onTabChange: (tab: 'replies' | 'reactions') => void
}) {
  const { t } = useTranslation()
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 })

  useEffect(() => {
    setTimeout(() => {
      const activeIndex = selectedTab === 'replies' ? 0 : 1
      if (activeIndex >= 0 && tabRefs.current[activeIndex]) {
        const activeTab = tabRefs.current[activeIndex]
        const { offsetWidth, offsetLeft } = activeTab
        const padding = 32 // 16px padding on each side
        setIndicatorStyle({
          width: offsetWidth - padding,
          left: offsetLeft + padding / 2
        })
      }
    }, 20) // ensure tabs are rendered before calculating
  }, [selectedTab])

  return (
    <div className="w-fit">
      <div className="flex relative">
        <div
          ref={(el) => (tabRefs.current[0] = el)}
          className={cn(
            `text-center px-4 py-2 font-semibold transition-colors hover:bg-muted/30 active:bg-muted/30 cursor-pointer rounded-lg`,
            selectedTab === 'replies' ? '' : 'text-muted-foreground'
          )}
          onClick={() => onTabChange('replies')}
        >
          {t('Replies')}
        </div>
        <div
          ref={(el) => (tabRefs.current[1] = el)}
          className={cn(
            `text-center px-4 py-2 font-semibold transition-colors hover:bg-muted/30 active:bg-muted/30 cursor-pointer rounded-lg`,
            selectedTab === 'reactions' ? '' : 'text-muted-foreground'
          )}
          onClick={() => onTabChange('reactions')}
        >
          {t('Reactions')}
        </div>
        <div
          className="absolute bottom-0 h-1 bg-primary rounded-full transition-all duration-500"
          style={{
            width: `${indicatorStyle.width}px`,
            left: `${indicatorStyle.left}px`
          }}
        />
      </div>
    </div>
  )
}
